import React, { useState } from 'react';
import {Wand2, CheckSquare, Gift, Clock, Sparkles, Loader2, Plus} from 'lucide-react';
import {aiGenerateAutomateMessage} from "../../api/message/automate-messages.api";

const CONDITIONS = [
    { id: 'BIRTHDAY', label: '생일 축하 메시지' },
    { id: 'RETARGETING', label: '재방문 유도' },
    { id: 'REMIND', label: '리마인드' },
    { id: 'EXPIRED_COUPON', label: '쿠폰 만료' },
    { id: 'RAINY_DAY', label: '비/눈오는날 유도' },
];

const QuickAutomationSetup = () => {
    const [selectedConditions, setSelectedConditions] = useState([]);
    const [commonExpiredDay, setCommonExpiredDay] = useState("14");
    const [couponName, setCouponName] = useState()
    const [requestMessage, setRequestMessage] = useState()
    const [showBirthdayCoupon, setShowBirthdayCoupon] = useState(false)
    const [birthdayCouponName, setBirthdayCouponName] = useState()

    // 로딩 상태 관리 추가
    const [isGenerating, setIsGenerating] = useState(false);

    const toggleCondition = (conditionId) => {
        if (isGenerating) return; // 로딩 중 조작 방지
        setSelectedConditions(prev =>
            prev.includes(conditionId)
                ? prev.filter(id => id !== conditionId)
                : [...prev, conditionId]
        );
    };

    const toggleAllConditions = () => {
        if (isGenerating) return;
        if (selectedConditions.length === CONDITIONS.length) {
            setSelectedConditions([]);
        } else {
            setSelectedConditions(CONDITIONS.map(c => c.id));
        }
    };

    const handleBulkApply = async () => {
        if (selectedConditions.length === 0) {
            alert("적용할 발송 조건을 하나 이상 선택해주세요.");
            return;
        }

        // 로딩 시작
        setIsGenerating(true);

        try {
            // onApplyAutoSetup이 Promise를 반환(async)한다고 가정하고 대기
            await aiGenerateAutomateMessage({
                conditions: selectedConditions,
                couponName: couponName,
                requestMessage: requestMessage,
                birthdayCouponName: birthdayCouponName,
                benefitExpiredDay: commonExpiredDay
            });
            // 완료 후 선택 초기화 (선택 사항)
            setSelectedConditions([]);
            alert("자동화 세팅이 완료되었습니다.");
            window.location.reload();
        } catch (error) {
            console.error(error);
            // 503(AI 생성 실패)이면 서버 사유가 error.message 에 있다. 그 외(네트워크 등)는 일반 문구.
            alert(error?.message || "AI 자동 생성 중 오류가 발생했습니다.");
        } finally {
            // 로딩 종료
            setIsGenerating(false);
        }
    };

    return (
        <div className="relative bg-gradient-to-br from-indigo-50/50 to-blue-50/50 border border-indigo-100 rounded-2xl p-6 shadow-sm mb-8 overflow-hidden">

            {/* 로딩 오버레이 창 */}
            {isGenerating && (
                <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-10 flex flex-col items-center justify-center transition-all duration-300">
                    <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mb-4" />
                    <h3 className="text-lg font-bold text-gray-900 mb-2">AI가 맞춤형 메시지와 이미지를 생성하고 있습니다</h3>
                    <p className="text-sm text-gray-600 text-center px-4">
                        선택하신 <span className="font-bold text-indigo-600">{selectedConditions.length}개</span>의 조건에 맞춰 작업을 진행 중입니다.<br/>
                        잠시만 기다려주세요...
                    </p>
                </div>
            )}

            <div className={`transition-opacity duration-300 ${isGenerating ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                            <Sparkles className="text-indigo-600" size={20} />
                            AI 빠른 자동화 일괄 세팅
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            조건과 혜택을 선택하면 AI가 상황에 맞는 이미지와 문구를 생성하고 일괄로 활성화합니다.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* 조건 선택 영역 */}
                    <div className="lg:col-span-2 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                                <CheckSquare size={16} className="text-gray-500" />
                                발송 조건 선택
                            </h3>
                            <button
                                onClick={toggleAllConditions}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium disabled:opacity-50"
                                disabled={isGenerating}
                            >
                                {selectedConditions.length === CONDITIONS.length ? "전체 해제" : "전체 선택"}
                            </button>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {CONDITIONS.map((condition) => {
                                const isSelected = selectedConditions.includes(condition.id);
                                return (
                                    <button
                                        key={condition.id}
                                        onClick={() => toggleCondition(condition.id)}
                                        disabled={isGenerating}
                                        className={`p-3 text-left rounded-xl text-sm transition-all border ${
                                            isSelected
                                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-md'
                                                : 'bg-white border-gray-200 text-gray-700 hover:border-indigo-300 hover:bg-indigo-50'
                                        }`}
                                    >
                                        <div className="font-medium">{condition.label}</div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* 공통 혜택 설정 및 실행 영역 */}
                    <div className="space-y-4 bg-white p-5 rounded-xl border border-gray-100 shadow-sm flex flex-col justify-between">
                        <div className="space-y-4">
                            <h3 className="text-sm font-bold text-gray-800 flex items-center gap-2 mb-3">
                                <Gift size={16} className="text-gray-500" />
                                공통 혜택 설정
                            </h3>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-600">지급할 쿠폰</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={couponName}
                                        onChange={(e) => setCouponName(e.target.value)}
                                        placeholder={`쿠폰명 입력 (예: 10% 할인)`}
                                        maxLength={25}
                                        className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all text-sm"
                                    />
                                </div>
                                {
                                    (showBirthdayCoupon && selectedConditions.includes("BIRTHDAY")) && (
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={birthdayCouponName}
                                                onChange={(e) => setBirthdayCouponName(e.target.value)}
                                                placeholder={`생일 쿠폰명 입력 (예: 10% 할인)`}
                                                maxLength={25}
                                                className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all text-sm"
                                            />
                                        </div>
                                    )
                                }
                                {
                                    (!showBirthdayCoupon && selectedConditions.includes("BIRTHDAY")) && (
                                        <button
                                            onClick={() => setShowBirthdayCoupon(true)}
                                            className="mt-3 flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium px-2 py-1 rounded hover:bg-indigo-50 transition-colors"
                                        >
                                            <Plus className="w-4 h-4" />
                                            생일 쿠폰 추가하기
                                        </button>
                                    )
                                }
                            </div>
                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-600 flex items-center gap-1">
                                    <Clock size={12} /> 유효 기간
                                </label>
                                <div className="flex gap-2 items-center">
                                    <span className="text-sm text-gray-600 whitespace-nowrap">발급 후</span>
                                    <div className="flex-1 flex items-center bg-white border border-gray-300 rounded-lg px-3 overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500">
                                        <input
                                            type="number"
                                            value={commonExpiredDay}
                                            onChange={(e) => setCommonExpiredDay(e.target.value)}
                                            disabled={isGenerating}
                                            className="w-full py-2 text-sm bg-transparent outline-none text-right pr-2 disabled:bg-white"
                                        />
                                        <span className="text-sm text-gray-500 whitespace-nowrap">일간</span>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <label className="text-xs font-semibold text-gray-600">요청 사항(선택)</label>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        value={requestMessage}
                                        onChange={(e) => setRequestMessage(e.target.value)}
                                        placeholder="일러스트레이션 느낌이 나야한다."
                                        className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 outline-none transition-all text-sm"
                                    />
                                </div>
                            </div>
                        </div>

                        {/* 실행 버튼 */}
                        <button
                            onClick={handleBulkApply}
                            disabled={isGenerating || (!couponName && !birthdayCouponName)}
                            className={`w-full mt-6 py-3.5 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all shadow-lg group
                                ${(isGenerating || (!couponName && !birthdayCouponName))
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : 'bg-gray-900 hover:bg-black text-white'
                            }`}
                        >
                            {isGenerating ? (
                                <>
                                    <Loader2 size={18} className="animate-spin" />
                                    생성 중...
                                </>
                            ) : (
                                <>
                                    <Wand2 size={18} className="group-hover:rotate-12 transition-transform" />
                                    AI 자동 생성 및 일괄 ON
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuickAutomationSetup;